import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { useCPPDynamicConfig } from '../../../hooks/useCPPDynamicConfig';
import useCollegeProspectAccess from '../../../hooks/useCollegeProspectAccess';
import { getApiAxiosInstance } from '../../../../utils/network';
import { showErrorPopup } from '../../../../actions/notification-actions';
import { CppCoachActions, CppCoachSurfaces } from '../../../Analytics/constants';
import useCPPTracking from '../../../hooks/useCPPTracking';
import VideoLinkList from './components/VideoLinkList';
import styles from '../collegeProspectTab.scss';

const TESTID_PREFIX = 'profile.student-info.college-prospect.recruiting-videos';
const HTTP_STATUS_FORBIDDEN = 403;

// AC: cpp_coach_recruiting_videos_viewed — IntersectionObserver at threshold
// 0.4, once per session per player-profile. Session-scoped dedup lives in
// sessionStorage so a Profile → Home → same-Profile revisit inside the same
// tab does not re-fire.
const IO_THRESHOLD = 0.4;
const VIEWED_SESSION_KEY_PREFIX = 'cpp.coachRecruitingVideosViewed:';

const hasFiredViewedThisSession = (subjectMemberId) => {
    if (typeof window === 'undefined' || !window.sessionStorage) {
        return false;
    }
    try {
        return window.sessionStorage.getItem(`${VIEWED_SESSION_KEY_PREFIX}${subjectMemberId}`) === '1';
    } catch {
        return false;
    }
};

const markViewedThisSession = (subjectMemberId) => {
    if (typeof window === 'undefined' || !window.sessionStorage) {
        return;
    }
    try {
        window.sessionStorage.setItem(`${VIEWED_SESSION_KEY_PREFIX}${subjectMemberId}`, '1');
    } catch {
        // sessionStorage disabled / full — treat as "may fire again". Acceptable
        // degradation: worst case we send a second event, we don't crash.
    }
};

const playerRecruitingVideosEndpoint = (memberId) => `/v1/cpp/player/${memberId}/recruiting-videos`;

const PlayerRecruitingVideos = ({ subjectMemberId, isSubjectCollegeProspect }) => {
    const dispatch = useDispatch();
    const { trackCoachEngagement } = useCPPTracking();
    const { isCollegeProspectPowerEnabled } = useCPPDynamicConfig();
    const { hasAnnualPower } = useCollegeProspectAccess();
    const isCppEligibleCoach = useSelector((state) => !!state.auth?.user?.isCollegeCoachCppEligible);
    const sectionRef = useRef(null);

    const canViewerRequestCppData = isCppEligibleCoach || hasAnnualPower;
    const shouldShow = isCollegeProspectPowerEnabled &&
        !!subjectMemberId &&
        isSubjectCollegeProspect &&
        canViewerRequestCppData;

    const [videos, setVideos] = useState(null);
    const [isViewDenied, setIsViewDenied] = useState(false);

    useEffect(() => {
        if (!shouldShow) {
            return undefined;
        }
        let cancelled = false;
        const fetchVideos = async () => {
            try {
                const response = await getApiAxiosInstance().get(playerRecruitingVideosEndpoint(subjectMemberId));
                if (cancelled) {
                    return;
                }
                setVideos(response.data?.items || []);
            } catch (err) {
                if (cancelled) {
                    return;
                }
                setIsViewDenied(true);
                if (err?.response?.status !== HTTP_STATUS_FORBIDDEN) {
                    dispatch(showErrorPopup({
                        message: 'We couldn\'t load this player\'s videos. Please try again.',
                        error: err,
                    }));
                }
            }
        };
        fetchVideos();
        return () => { cancelled = true; };
    }, [shouldShow, subjectMemberId, dispatch]);

    const sectionIsVisible = shouldShow && !isViewDenied && videos !== null && videos.length > 0;

    useEffect(() => {
        if (!sectionIsVisible) {
            return undefined;
        }
        if (hasFiredViewedThisSession(subjectMemberId)) {
            return undefined;
        }
        const node = sectionRef.current;
        if (!node) {
            return undefined;
        }
        // Fallback for older browsers / test envs: fire on mount if
        // IntersectionObserver isn't available.
        if (typeof window === 'undefined' || typeof window.IntersectionObserver !== 'function') {
            markViewedThisSession(subjectMemberId);
            trackCoachEngagement(
                CppCoachActions.CPP_COACH_RECRUITING_VIDEOS_VIEWED,
                CppCoachSurfaces.PLAYER_PROFILE,
                {
                    playerId: subjectMemberId,
                    video_count: videos?.length ?? 0,
                },
            );
            return undefined;
        }
        const observer = new window.IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting || entry.intersectionRatio < IO_THRESHOLD) {
                        return;
                    }
                    if (hasFiredViewedThisSession(subjectMemberId)) {
                        observer.disconnect();
                        return;
                    }
                    markViewedThisSession(subjectMemberId);
                    trackCoachEngagement(
                        CppCoachActions.CPP_COACH_RECRUITING_VIDEOS_VIEWED,
                        CppCoachSurfaces.PLAYER_PROFILE,
                        {
                            playerId: subjectMemberId,
                            video_count: videos?.length ?? 0,
                        },
                    );
                    observer.disconnect();
                });
            },
            { threshold: IO_THRESHOLD },
        );
        observer.observe(node);
        return () => observer.disconnect();
    // trackCoachEngagement re-creates every render; the session-dedup gate is
    // what actually prevents re-fires across mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sectionIsVisible, subjectMemberId, videos?.length]);

    const handleVideoPlayed = useCallback(({ videoId, host, videoIndex }) => {
        trackCoachEngagement(
            CppCoachActions.CPP_COACH_VIDEO_PLAYED,
            CppCoachSurfaces.PLAYER_PROFILE,
            {
                playerId: subjectMemberId,
                video_id: videoId,
                host,
                video_index: videoIndex,
            },
        );
    // trackCoachEngagement re-creates every render — subjectMemberId is the only stable dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subjectMemberId]);

    if (!sectionIsVisible) {
        return null;
    }

    return (
        <div ref={sectionRef} data-testid={`${TESTID_PREFIX}.section`}>
            <h6
                className="text-semi mt48"
                data-testid={`${TESTID_PREFIX}.title`}
            >
                Video
            </h6>
            <div className={styles.playerRecruitingVideosCard}>
                <VideoLinkList
                    videos={videos}
                    onVideoPlayed={handleVideoPlayed}
                    data-testid={TESTID_PREFIX}
                />
            </div>
        </div>
    );
};

PlayerRecruitingVideos.propTypes = {
    subjectMemberId: PropTypes.number,
    isSubjectCollegeProspect: PropTypes.bool,
};

PlayerRecruitingVideos.defaultProps = {
    subjectMemberId: null,
    isSubjectCollegeProspect: false,
};

export default PlayerRecruitingVideos;
